/* eslint-disable react-hooks/exhaustive-deps */
import { logout } from '@/api/login';
import useSnackbar from '@/hooks/useSnackbar';
import { Tooltip } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';

import { StatusCode, WebsocketTypes } from '../../api/enumerations';
import { getMessagesUnread } from '../../api/theHive/workOrders';
import {
  IconAccountBalanceMS,
  IconArchiveMS,
  IconDashboardMS,
  IconHomeMS,
  IconHubMS,
  IconListAltMS,
  IconLogoutMS,
  IconKeyboardDoubleArrowLeftMS,
  IconKeyboardDoubleArrowRightMS,
  IconNotificationsMS,
  IconPersonMS,
  IconPropellerMS,
} from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import {
  useWebsocketEvent,
  useWebsocketPublisher,
} from '../../context/websocketMessage';
import { matchCompanySubdomain } from '../../helpers';
import { removeLocalStorageToken } from '../../helpers/localStorage';
import { AccessKey, canSee } from '../../helpers/roleAccess';
import { useCurrentRole } from '../../hooks/useCurrentRole';
import { useStoragedJwt } from '../../hooks/useDecodedJwt';
import useErrorMessage from '../../hooks/useErrorMessage';
import { useRecruitmentAlerts } from '../../hooks/useRecruitmentAlerts';
import { useReturnedReports } from '../../hooks/useReturnedReports';
import { AvaliaHubLogo } from '../Brand';
import {
  Avatar,
  CollapsedDot,
  CountBadge,
  Logo,
  LogoMarkButton,
  LogoRow,
  NavButton,
  NavItem,
  NavItemWrap,
  NavLabel,
  NavList,
  OutletContainer,
  Sidebar,
  SidebarFooter,
  ToggleButton,
  UserEmail,
  UserName,
  UserRow,
  UserText,
} from './styles';

// Session-scoped: a new tab always opens with the sidebar expanded.
const SIDEBAR_STORAGE_KEY = 'shell:sidebar-open';

const clearStoredSession = (): void => {
  removeLocalStorageToken();
};

const readSidebarPreference = (): boolean => {
  try {
    return window.sessionStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'closed';
  } catch {
    return true;
  }
};

const initials = (
  name: string | undefined,
  email: string | undefined
): string => {
  const source = (name && name.trim()) || email || '';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
};

interface MenuEntry {
  name: string;
  icon: JSX.Element;
  url: string;
  accessKey: AccessKey;
}

// Sentence case, product vocabulary (guide 9.6). Order = frequency of use.
const menuEntries: MenuEntry[] = [
  { name: 'Início', icon: IconHomeMS, url: '/home', accessKey: 'home' },
  {
    name: 'Dashboard',
    icon: IconDashboardMS,
    url: '/dashboard',
    accessKey: 'dashboard',
  },
  {
    name: 'Arquivados',
    icon: IconArchiveMS,
    url: '/archived',
    accessKey: 'archive',
  },
  { name: 'The Hive', icon: IconHubMS, url: '/the-hive', accessKey: 'hive' },
  {
    name: 'Pré-flight',
    icon: IconPropellerMS,
    url: '/pre-flight',
    accessKey: 'preflight',
  },
  {
    name: 'Notificações',
    icon: IconNotificationsMS,
    url: '/notifications',
    accessKey: 'notifications',
  },
  {
    name: 'Formulários',
    icon: IconListAltMS,
    url: '/forms',
    accessKey: 'forms',
  },
  {
    name: 'Usuários',
    icon: IconPersonMS,
    url: '/users-roles',
    accessKey: 'userPanel',
  },
  {
    name: 'Financeiro',
    icon: IconAccountBalanceMS,
    url: '/finance',
    accessKey: 'finance',
  },
];

export default function Menu(): JSX.Element {
  const [open, setOpen] = useState<boolean>(readSidebarPreference);
  const [unreadCount, setUnreadCount] = useState(0);

  const { setBankCompanyData } = useContext(GlobalContext);
  const publish = useWebsocketPublisher();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getErrorMessage } = useErrorMessage();
  const decoded = useStoragedJwt();
  const role = useCurrentRole();
  // "Laudo devolvido" (pre-flight) shares the bell with the Hive messages.
  const { unread: returnedUnread } = useReturnedReports();
  // "Par crítico" of the Preenchimento page (REAL-3273), admins only.
  const { unread: recruitmentUnread } = useRecruitmentAlerts();
  const { lastMessage } = useWebSocket(
    `${process.env.REACT_APP_API_WEBSOCKET_URL}/websockets/${decoded?.user.id}`
  );

  const visibleEntries = menuEntries.filter((item) =>
    canSee(item.accessKey, role)
  );

  const toggleSidebar = (): void => {
    setOpen((current) => {
      const next = !current;
      try {
        window.sessionStorage.setItem(
          SIDEBAR_STORAGE_KEY,
          next ? 'open' : 'closed'
        );
      } catch {
        // Preference is a convenience; ignore storage failures.
      }
      return next;
    });
  };

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      const { detail } = await logout();
      if (detail?.description) {
        throw new Error(detail.description);
      }
      if (detail?.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      clearStoredSession();
      window.location.reload();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      clearStoredSession();
      navigate('/login');
    }
  }, [handleSnackbar, getErrorMessage, navigate]);

  const fetchUnreadMessages = useCallback(async () => {
    try {
      const unreadNotifications = await getMessagesUnread();
      if (unreadNotifications.detail.description) {
        throw new Error(unreadNotifications.detail.description);
      }
      if (unreadNotifications.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      if (unreadNotifications.data) {
        setUnreadCount(unreadNotifications.data.total_count);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, []);

  useEffect(() => {
    if (!decoded) return;
    const companySubdomain = matchCompanySubdomain();
    const currentCompanyData = decoded.user.companies.find(
      (company) => company.subdomain === companySubdomain
    );

    if (currentCompanyData) {
      if (currentCompanyData.is_hive_owner) {
        setBankCompanyData(currentCompanyData);
        fetchUnreadMessages();
      }
    }
  }, []);

  useEffect(() => {
    if (lastMessage) publish(lastMessage);
  }, [lastMessage, publish]);

  useWebsocketEvent(WebsocketTypes.NEW_MESSAGE, ({ payload }) => {
    const count = Number(payload?.unread_messages_count) || 0;
    setUnreadCount((prev) => {
      const newCount = payload?.read_messages ? prev - count : prev + count;
      return Math.max(newCount, 0);
    });
  });

  const userName = decoded?.user.name;
  const userEmail = decoded?.user.email;

  const renderEntry = (entry: MenuEntry): JSX.Element => {
    const active =
      pathname === entry.url || pathname.startsWith(`${entry.url}/`);
    const notificationsCount = unreadCount + returnedUnread + recruitmentUnread;
    const showCount =
      entry.accessKey === 'notifications' && notificationsCount > 0;

    const item = (
      <NavItem
        to={entry.url}
        active={active}
        open={open}
        aria-current={active ? 'page' : undefined}
        aria-label={open ? undefined : entry.name}
      >
        {entry.icon}
        {open && <NavLabel>{entry.name}</NavLabel>}
        {open && showCount && <CountBadge>{notificationsCount}</CountBadge>}
      </NavItem>
    );

    return (
      <NavItemWrap key={entry.url}>
        {open ? (
          item
        ) : (
          <Tooltip title={entry.name} placement="right">
            {item}
          </Tooltip>
        )}
        {!open && showCount && <CollapsedDot aria-hidden="true" />}
      </NavItemWrap>
    );
  };

  return (
    <>
      <Sidebar open={open} aria-label="navegação principal">
        <LogoRow open={open}>
          {!open && (
            <ToggleButton
              type="button"
              open={open}
              aria-label="Expandir menu"
              aria-expanded={false}
              title="Expandir menu"
              onClick={toggleSidebar}
            >
              {IconKeyboardDoubleArrowRightMS}
            </ToggleButton>
          )}
          {open ? (
            <Logo>
              <AvaliaHubLogo
                variant="lockup"
                tone="onLight"
                height={28}
                title="AvaliaHub"
              />
            </Logo>
          ) : (
            <LogoMarkButton
              type="button"
              aria-label="Expandir menu"
              aria-expanded={false}
              title="Expandir menu"
              onClick={toggleSidebar}
            >
              <AvaliaHubLogo variant="mark" height={24} />
            </LogoMarkButton>
          )}
          {open && (
            <ToggleButton
              type="button"
              open={open}
              aria-label="Recolher menu"
              aria-expanded
              title="Recolher menu"
              onClick={toggleSidebar}
            >
              {IconKeyboardDoubleArrowLeftMS}
            </ToggleButton>
          )}
        </LogoRow>

        <NavList>{visibleEntries.map(renderEntry)}</NavList>

        <SidebarFooter>
          <UserRow open={open}>
            <Avatar aria-hidden="true">{initials(userName, userEmail)}</Avatar>
            {open && (
              <UserText>
                <UserName>{userName || userEmail}</UserName>
                {userName && <UserEmail>{userEmail}</UserEmail>}
              </UserText>
            )}
          </UserRow>
          <Tooltip title="Sair" placement="right">
            <NavButton
              type="button"
              active={false}
              open={open}
              onClick={handleLogout}
              aria-label="Sair"
            >
              {IconLogoutMS}
              {open && <NavLabel>Sair</NavLabel>}
            </NavButton>
          </Tooltip>
        </SidebarFooter>
      </Sidebar>
      <OutletContainer open={open}>
        <Outlet />
      </OutletContainer>
    </>
  );
}
